// ── 변경이력 모달 + 수정세금계산서 입금이관 모달 ──────────────────────────────
import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { fmtNum, fmtDate, overlayStyle, modalBtns, btnCancel, btnPrimary, labelTxt, fullSelect } from './styles';
import { ACTION_CFG, renderDiff, renderPaymentDiff, renderTaxInvoiceDiff, renderBatchDiff } from './auditDiff';
import { auditBaseTable } from './auditFieldLabels';

/**
 * 수정 세금계산서 등록 후 "원본의 입금을 이쪽으로 옮기시겠습니까?" 팝업
 */
export function PaymentMigrationModal({ modifiedInvoice, candidates, onConfirm, onSkip }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onSkip);
  const [selectedId, setSelectedId] = useState('');

  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 24, maxWidth: 520, width: '95vw',
        maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 6 }}>
          📝 수정 세금계산서가 등록됐습니다
        </div>
        {/* 수정본 요약 */}
        <div style={{ fontSize: 12, color: 'var(--text-sub)', background: 'var(--bg-subtle)', borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
          {fmtDate(modifiedInvoice.issueDate)} · <strong>{modifiedInvoice.customerName}</strong> · {fmtNum(modifiedInvoice.totalAmount)}원
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-sub)', marginBottom: 12, lineHeight: 1.6 }}>
          원본 세금계산서에 연결된 입금을 이 수정본으로 옮기시겠습니까?<br />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>원본을 선택하면 입금이 자동으로 이관됩니다.</span>
        </div>
        {/* 원본 후보 목록 */}
        {candidates.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>같은 상호의 원본 세금계산서가 없습니다.</div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <label style={labelTxt}>원본 세금계산서 선택</label>
            <select style={fullSelect} value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">선택하세요</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  [{fmtDate(c.issue_date)}] {c.customer_name} · {fmtNum(c.total_amount)}원
                  {Number(c.unpaid_amount) === 0 ? ' ✅ 완납' : ` (미입금 ${fmtNum(c.unpaid_amount)}원)`}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={modalBtns}>
          <button style={btnCancel} onClick={onSkip}>나중에 처리</button>
          <button style={{ ...btnPrimary, opacity: selectedId ? 1 : 0.4 }} disabled={!selectedId}
            onClick={() => onConfirm(Number(selectedId))}>
            입금 이관
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 변경이력 모달 — 타임라인 형태로 표시
 */
// apiBase: 회사별 매출 주소(2026-11-05) — 그 회사 열쇠로 그 회사 이력만 본다. 예전엔 어느 회사 화면이든 본사 주소를 불렀다.
export function AuditLogModal({ title, tableName, onClose, apiBase = '/api/admin/revenue' }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await apiClient.get(`${apiBase}/audit-logs?table_name=${encodeURIComponent(tableName)}`);
      setRows(r.data?.data || []);
      setLoading(false);
    })();
  }, [apiBase, tableName]);

  return (
    <div style={{ ...overlayStyle, alignItems: 'flex-start', paddingTop: 40 }} {...overlayCloseProps}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 24, width: '96vw', maxWidth: 720,
        maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
        onClick={(e) => e.stopPropagation()}>
        {/* 모달 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-sub)' }}>이력 — {title}</div>
          <button style={btnCancel} onClick={onClose}>닫기</button>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>로딩 중...</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>이력이 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((row) => {
              const cfg = ACTION_CFG[row.action] || { label: row.action, color: 'var(--text-sub)', bg: 'var(--bg-subtle)', border: 'var(--border)' };
              return (
                <div key={row.id} style={{ borderLeft: `3px solid ${cfg.border}`,
                  paddingLeft: 14, paddingTop: 10, paddingBottom: 10, paddingRight: 12,
                  background: 'var(--bg-subtle)', borderRadius: '0 8px 8px 0' }}>
                  {/* 항목 헤더: 배지 + 작업자 + 일시 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4,
                      background: cfg.bg, color: cfg.color, fontWeight: 700, flexShrink: 0 }}>
                      {cfg.label}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text-sub)', fontWeight: 600 }}>{row.actor_name || '-'}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{row.created_at}</span>
                  </div>
                  {/* 변경 내용: 배치 이벤트/입금/세금계산서는 전용 렌더러, 나머지는 일반 diff */}
                  {(row.action === 'upload' || row.action === 'batch_delete' || row.action === 'download')
                    ? renderBatchDiff(row)
                    : auditBaseTable(tableName) === 'revenue_payments'
                      ? renderPaymentDiff(row)
                      : auditBaseTable(tableName) === 'revenue_tax_invoices'
                        ? renderTaxInvoiceDiff(row)
                        : renderDiff(row, tableName)
                  }
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
